import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, Lock, ArrowRight, Sparkles, Shield, KeyRound, RotateCcw } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import SupportWidget from '../components/SupportWidget';
import { getOAuthErrorMessage } from '../lib/utils';
import SEOHead from '../components/SEOHead';
import { getMarketingSeoConfig } from '../lib/seo';
import { BETA_AUTH_BYPASS_ENABLED } from '../lib/featureFlags';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingPlan, setPendingPlan] = useState(null);
  const [authStep, setAuthStep] = useState('credentials');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorChallengeId, setTwoFactorChallengeId] = useState('');
  const [twoFactorMaskedEmail, setTwoFactorMaskedEmail] = useState('');
  const [rememberDeviceAvailable, setRememberDeviceAvailable] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(true);
  const [verifying2FA, setVerifying2FA] = useState(false);
  const [resending2FA, setResending2FA] = useState(false);
  const [betaBypassLoading, setBetaBypassLoading] = useState(false);

  const [showResetFlow, setShowResetFlow] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [resetChallengeId, setResetChallengeId] = useState('');
  const [resetMaskedEmail, setResetMaskedEmail] = useState('');
  const [resetStep, setResetStep] = useState('request');
  const [resetLoading, setResetLoading] = useState(false);

  const {
    login,
    betaBypassLogin,
    loginWithGoogle,
    verifyTwoFactor,
    resendTwoFactorCode,
    requestPasswordReset,
    confirmPasswordReset,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const seoConfig = getMarketingSeoConfig('login');

  useEffect(() => {
    setPendingPlan(sessionStorage.getItem('selectedPlan'));
  }, []);

  useEffect(() => {
    const oauthError = searchParams.get('oauth_error');
    if (oauthError) {
      toast.error(getOAuthErrorMessage(oauthError));
    }
  }, [searchParams]);

  const resolvePostAuthPath = () => {
    const stateNext = location.state?.from;
    const queryNext = searchParams.get('next');

    if (queryNext) {
      return queryNext;
    }

    if (stateNext?.pathname) {
      return `${stateNext.pathname}${stateNext.search || ''}${stateNext.hash || ''}`;
    }

    return '/dashboard';
  };

  const handleGoogleLogin = () => {
    loginWithGoogle(resolvePostAuthPath(), { flow: 'login' });
  };

  const handlePartnerPortal = () => {
    navigate('/fornecedor/login');
  };

  const handleBetaBypass = async () => {
    setBetaBypassLoading(true);
    try {
      await betaBypassLogin();
      toast.success('Acesso beta liberado com sucesso.');
      navigate(resolvePostAuthPath());
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível liberar o acesso beta.');
    } finally {
      setBetaBypassLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.requires_2fa) {
        setAuthStep('verify_2fa');
        setTwoFactorChallengeId(result.challenge_id || '');
        setTwoFactorMaskedEmail(result.masked_email || email);
        setRememberDeviceAvailable(Boolean(result?.remember_device_available));
        setRememberDevice(Boolean(result?.remember_device_available));
        setTwoFactorCode('');
        toast.success('Código de verificação enviado para o seu e-mail.');
        return;
      }

      toast.success('Bem-vindo(a) de volta!');

      const pendingAffiliateCode = sessionStorage.getItem('affiliateCode');
      const selectedPlan = sessionStorage.getItem('selectedPlan');
      if (selectedPlan) {
        const queryString = pendingAffiliateCode ? `?ref=${encodeURIComponent(pendingAffiliateCode)}` : '';
        navigate(`/planos${queryString}`);
        return;
      }

      navigate(resolvePostAuthPath());
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyTwoFactor = async (event) => {
    event.preventDefault();
    if ((twoFactorCode || '').replace(/\D/g, '').length !== 6) {
      toast.error('Informe o código de 6 dígitos enviado para o seu e-mail.');
      return;
    }

    setVerifying2FA(true);
    try {
      await verifyTwoFactor(twoFactorChallengeId, twoFactorCode, rememberDeviceAvailable && rememberDevice);
      toast.success('Autenticação concluída com sucesso.');

      const pendingAffiliateCode = sessionStorage.getItem('affiliateCode');
      const selectedPlan = sessionStorage.getItem('selectedPlan');
      if (selectedPlan) {
        const queryString = pendingAffiliateCode ? `?ref=${encodeURIComponent(pendingAffiliateCode)}` : '';
        navigate(`/planos${queryString}`);
        return;
      }

      navigate(resolvePostAuthPath());
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível validar o código.');
    } finally {
      setVerifying2FA(false);
    }
  };

  const handleResendTwoFactor = async () => {
    if (!twoFactorChallengeId) return;
    setResending2FA(true);
    try {
      const result = await resendTwoFactorCode(twoFactorChallengeId);
      setTwoFactorChallengeId(result?.challenge_id || twoFactorChallengeId);
      setTwoFactorMaskedEmail(result?.masked_email || twoFactorMaskedEmail);
      setRememberDeviceAvailable(Boolean(result?.remember_device_available));
      setRememberDevice(Boolean(result?.remember_device_available));
      setTwoFactorCode('');
      toast.success('Novo código enviado para o e-mail cadastrado.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível reenviar o código.');
    } finally {
      setResending2FA(false);
    }
  };

  const handleRequestReset = async (event) => {
    event.preventDefault();
    if (!resetEmail.trim()) {
      toast.error('Informe o e-mail da conta para receber o código.');
      return;
    }

    setResetLoading(true);
    try {
      const result = await requestPasswordReset(resetEmail.trim());
      toast.success(result?.message || 'Se o e-mail estiver cadastrado, você receberá um código.');

      if (result?.challenge_id) {
        setResetChallengeId(result.challenge_id);
        setResetMaskedEmail(result.masked_email || resetEmail.trim());
        setResetStep('confirm');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível iniciar a redefinição de senha.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleConfirmReset = async (event) => {
    event.preventDefault();
    if ((resetCode || '').replace(/\D/g, '').length !== 6) {
      toast.error('Informe o código de 6 dígitos.');
      return;
    }
    if ((resetNewPassword || '').trim().length < 6) {
      toast.error('A nova senha deve ter pelo menos 6 caracteres.');
      return;
    }
    if (resetNewPassword !== resetConfirmPassword) {
      toast.error('As senhas não coincidem.');
      return;
    }

    setResetLoading(true);
    try {
      const result = await confirmPasswordReset(resetChallengeId, resetCode, resetNewPassword);
      toast.success(result?.message || 'Senha redefinida com sucesso.');
      setShowResetFlow(false);
      setResetStep('request');
      setResetCode('');
      setResetNewPassword('');
      setResetConfirmPassword('');
      setResetChallengeId('');
      setResetMaskedEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível redefinir a senha.');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F6F1EF] flex">
      <SEOHead
        title={seoConfig.title}
        description={seoConfig.description}
        canonical={seoConfig.canonical}
        jsonLd={seoConfig.jsonLd}
      />
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1519741497674-611481863552?w=1200)' }}
        />
        <div className="absolute inset-0 bg-[#E8B7B1]/60" />
        <div className="relative z-10 flex flex-col justify-center items-center text-white p-12">
          <BrandLogo size="md" withText={false} iconClassName="w-16 h-16 mb-6" />
          <h1 className="text-4xl font-bold text-center mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Sua lista de casamento
          </h1>
          <p className="text-lg text-center text-white/80 max-w-md">
            Crie um site personalizado para seu casamento e facilite a vida dos seus convidados.
          </p>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-[#1C1C1C] mb-6">
              <BrandLogo size="sm" />
            </Link>
            <h2 className="text-3xl font-bold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display' }}>
              Entrar na sua conta
            </h2>
            <p className="text-[#333333] mt-2">
              Acesse seu painel e gerencie seu casamento
            </p>
            {pendingPlan && (
              <p className="mt-3 text-sm text-[#1C1C1C] bg-[#C8A97E]/20 border border-[#C8A97E]/30 rounded-lg px-3 py-2">
                Entre para continuar a ativação do seu plano grátis.
              </p>
            )}
            {authStep === 'verify_2fa' && (
              <p className="mt-3 text-sm text-[#1C1C1C] bg-[#E8B7B1]/20 border border-[#E8B7B1]/35 rounded-lg px-3 py-2">
                Enviamos um código para <strong>{twoFactorMaskedEmail}</strong>.
              </p>
            )}
          </div>

          {authStep === 'credentials' ? (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                  E-mail
                </Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#333333]" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="seu@email.com"
                    autoComplete="email"
                    className="pl-12 h-12 rounded-lg border-slate-200 bg-slate-50 focus:border-[#E8B7B1] focus:ring-[#E8B7B1]"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                  Senha
                </Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#333333]" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="Digite sua senha"
                    autoComplete="current-password"
                    className="pl-12 h-12 rounded-lg border-slate-200 bg-slate-50 focus:border-[#E8B7B1] focus:ring-[#E8B7B1]"
                    required
                    data-testid="login-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-[#E8B7B1] hover:bg-[#DFA8A1] text-white rounded-full font-bold tracking-wider transition-colors duration-300"
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Entrando...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Entrar <ArrowRight className="w-5 h-5" />
                  </span>
                )}
              </Button>

              {BETA_AUTH_BYPASS_ENABLED ? (
                <div className="rounded-2xl border border-[#C8A97E]/30 bg-[#FFF7F1] p-4">
                  <p className="text-sm font-semibold text-[#1C1C1C]">
                    Acesso rápido de homologação
                  </p>
                  <p className="mt-1 text-sm text-[#4A4036]">
                    Use este atalho apenas para validar a beta sem depender do envio do código por e-mail.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleBetaBypass}
                    disabled={betaBypassLoading}
                    className="mt-3 w-full rounded-full border-[#C8A97E] text-[#1C1C1C] hover:bg-[#C8A97E]/10"
                    data-testid="beta-bypass-btn"
                  >
                    {betaBypassLoading ? 'Liberando acesso...' : 'Entrar com acesso beta'}
                  </Button>
                </div>
              ) : null}
            </form>
          ) : (
            <form onSubmit={handleVerifyTwoFactor} className="space-y-5">
              <div>
                <Label htmlFor="otp" className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                  Código de verificação
                </Label>
                <div className="relative">
                  <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#333333]" />
                  <Input
                    id="otp"
                    type="text"
                    inputMode="numeric"
                    value={twoFactorCode}
                    onChange={(event) => setTwoFactorCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    autoComplete="one-time-code"
                    className="pl-12 h-12 rounded-lg border-slate-200 bg-slate-50 tracking-[0.4em] text-center font-semibold focus:border-[#E8B7B1] focus:ring-[#E8B7B1]"
                    required
                    data-testid="login-otp-input"
                  />
                </div>
              </div>

              {rememberDeviceAvailable ? (
                <label className="flex items-start gap-3 rounded-lg border border-[#E8B7B1]/35 bg-white/60 p-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberDevice}
                    onChange={(event) => setRememberDevice(event.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-[#E8B7B1] focus:ring-[#E8B7B1]"
                  />
                  <span className="text-sm text-[#333333]">
                    Não perguntar novamente neste dispositivo por 30 dias
                  </span>
                </label>
              ) : (
                <div className="rounded-lg border border-[#C8A97E]/25 bg-[#C8A97E]/10 px-4 py-3 text-sm text-[#4A4036]">
                  Esse código é obrigatório nesta validação inicial. Depois disso, você poderá marcar o dispositivo como confiável.
                </div>
              )}

              <Button
                type="submit"
                disabled={verifying2FA}
                className="w-full h-12 bg-[#E8B7B1] hover:bg-[#DFA8A1] text-white rounded-full font-bold tracking-wider transition-colors duration-300"
                data-testid="login-2fa-submit-btn"
              >
                {verifying2FA ? (
                  <span className="flex items-center gap-2">
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Validando...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Validar código <ArrowRight className="w-5 h-5" />
                  </span>
                )}
              </Button>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleResendTwoFactor}
                  disabled={resending2FA}
                  className="flex-1 rounded-full"
                >
                  {resending2FA ? 'Reenviando...' : 'Reenviar código'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setAuthStep('credentials');
                    setTwoFactorCode('');
                    setTwoFactorChallengeId('');
                  }}
                  className="flex-1 rounded-full"
                >
                  Voltar
                </Button>
              </div>
            </form>
          )}

          {authStep === 'credentials' && (
            <>
              <div className="relative my-8">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-[#F6F1EF] text-[#333333]">ou continue com</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={handleGoogleLogin}
                className="w-full h-12 border-2 border-slate-200 hover:bg-slate-50 rounded-full font-medium transition-colors duration-300"
                data-testid="login-google-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continuar com Google
              </Button>

              <div className="flex items-center justify-between mt-5">
                <p className="text-[#333333] text-sm">
                  Não tem uma conta?{' '}
                  <Link to="/register" className="text-[#1C1C1C] font-bold hover:underline" data-testid="register-link">
                    Criar conta
                  </Link>
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setShowResetFlow((previous) => !previous);
                    setResetStep('request');
                    setResetCode('');
                    setResetNewPassword('');
                    setResetConfirmPassword('');
                    setResetChallengeId('');
                    setResetMaskedEmail('');
                  }}
                  className="text-sm font-semibold text-[#B97D5B] hover:underline"
                >
                  Esqueci minha senha
                </button>
              </div>
            </>
          )}

          {showResetFlow && authStep === 'credentials' && (
            <div className="mt-6 rounded-2xl border border-[#E8B7B1]/35 bg-white p-4">
              <div className="flex items-center gap-2 text-[#1C1C1C] mb-3">
                <RotateCcw className="w-4 h-4" />
                <p className="font-semibold">Redefinir senha com verificação 2FA</p>
              </div>

              {resetStep === 'request' ? (
                <form onSubmit={handleRequestReset} className="space-y-3">
                  <Label htmlFor="reset-email" className="text-sm font-semibold text-[#333333]">
                    E-mail da conta
                  </Label>
                  <Input
                    id="reset-email"
                    type="email"
                    value={resetEmail}
                    onChange={(event) => setResetEmail(event.target.value)}
                    placeholder="seu@email.com"
                    autoComplete="email"
                    className="h-11"
                    required
                  />
                  <Button type="submit" disabled={resetLoading} className="w-full rounded-full bg-[#E8B7B1] hover:bg-[#DFA8A1] text-white">
                    {resetLoading ? 'Enviando código...' : 'Enviar código de redefinição'}
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleConfirmReset} className="space-y-3">
                  <p className="text-sm text-[#333333]">
                    Código enviado para <strong>{resetMaskedEmail || resetEmail}</strong>.
                  </p>
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={resetCode}
                    onChange={(event) => setResetCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Código de 6 dígitos"
                    autoComplete="one-time-code"
                    className="h-11 tracking-[0.2em] text-center font-semibold"
                    required
                  />
                  <Input
                    type="password"
                    value={resetNewPassword}
                    onChange={(event) => setResetNewPassword(event.target.value)}
                    placeholder="Nova senha"
                    autoComplete="new-password"
                    className="h-11"
                    required
                  />
                  <Input
                    type="password"
                    value={resetConfirmPassword}
                    onChange={(event) => setResetConfirmPassword(event.target.value)}
                    placeholder="Confirmar nova senha"
                    autoComplete="new-password"
                    className="h-11"
                    required
                  />
                  <Button type="submit" disabled={resetLoading} className="w-full rounded-full bg-[#E8B7B1] hover:bg-[#DFA8A1] text-white">
                    {resetLoading ? 'Redefinindo...' : 'Redefinir senha'}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full rounded-full"
                    onClick={() => {
                      setResetStep('request');
                      setResetCode('');
                    }}
                  >
                    Voltar para solicitar novo código
                  </Button>
                </form>
              )}
            </div>
          )}

          <div className="mt-8 pt-6 border-t border-slate-200">
            <p className="text-center text-sm text-[#333333] mb-3">
              Trabalha com casamentos?
            </p>
            <Button
              type="button"
              variant="ghost"
              onClick={handlePartnerPortal}
              className="w-full h-11 text-[#C8A97E] hover:bg-[#C8A97E]/10 rounded-full font-medium transition-colors duration-300"
              data-testid="supplier-portal-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Acessar Portal de Parceiros
            </Button>
            <p className="text-center text-xs text-[#333333] mt-2">
              Indique casais e acompanhe seu cupom oficial
            </p>
          </div>

          <div className="mt-4">
            <Link to="/admin/login" className="block">
              <Button
                type="button"
                variant="ghost"
                className="w-full h-10 text-[#333333] hover:bg-slate-100 rounded-full text-sm"
                data-testid="admin-portal-btn"
              >
                <Shield className="w-4 h-4 mr-2" />
                Acessar Painel Admin
              </Button>
            </Link>
          </div>
        </div>
      </div>
      <SupportWidget />
    </div>
  );
}
